import { filterEmptyValues, url } from '@/helpers';
import { getSerpSeoDescription, getSerpSeoTitle } from '@/helpers/seoTs';
import { SearchManagerOutput } from '@/hooks/search/useSearch';
import { DEFAULT_SEARCH_KEYWORD, DEFAULT_SEARCH_LOCATION } from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import { BaseSearchManagerOutput } from '@/hooks/search/useSearch.types';
import { getLang, _s, _t } from '@/locale';
import { TimeOfDayOption } from '@/types/state/search';
import { SEARCH_RESULT_PER_PAGE_LIMIT } from '../constants';

export function hasNextPage(results: number, page: number, itemsPerPage: number): boolean {
  return (+page + 1) * itemsPerPage < results;
}

function getPaginationDescription(search: SearchManagerOutput): string {
  if (search.page > 0) {
    return _t('seo.from-to', {
      from: search.page * SEARCH_RESULT_PER_PAGE_LIMIT + 1,
      to:
        search.page * SEARCH_RESULT_PER_PAGE_LIMIT + SEARCH_RESULT_PER_PAGE_LIMIT > search.results
          ? search.results
          : search.page * SEARCH_RESULT_PER_PAGE_LIMIT + SEARCH_RESULT_PER_PAGE_LIMIT,
      of: search.results,
    });
  }
  return '';
}

export function getSeoProps(search: SearchManagerOutput, location /** React router location */) {
  let pathname: string = '';
  if (search.type === 'sistaminuten') {
    pathname = 'sistaminuten/';
  }

  const _keyword = getKeywordWithSlug(search.q)?.slug || DEFAULT_SEARCH_KEYWORD;
  const _location = getLocationWithSlug(search.location)?.slug || DEFAULT_SEARCH_LOCATION;

  pathname += `${_keyword}/${_location}`;

  const fullURL = `${url.getBaseUrl()}${pathname}${location.search}`;
  const page = Math.max(0, Number(search.page) || 0);
  const canonical = `${url.getBaseUrl()}${pathname}${page > 0 ? '?page=' + (page + 1) : ''}`;

  const buildPageUrl = (pageNumber: number): string => {
    // Remove page parameter for first page
    if (pageNumber === 0) {
      return fullURL.replace(/[?&]page=\d+/, '').replace(/\?$/, '');
    }

    // use URL 1-based indexing page numbers
    const urlPageNumber = pageNumber + 1;

    const hasPageParam = fullURL.indexOf('page=') !== -1;
    if (hasPageParam) {
      return fullURL.replace(/page=\d+/, `page=${urlPageNumber}`);
    }

    const hasQueryParams = fullURL.indexOf('?') !== -1;
    if (hasQueryParams) {
      return fullURL.replace(/&$/, '') + `&page=${urlPageNumber}`;
    }

    return fullURL + `?page=${urlPageNumber}`;
  };

  const prev = (() => {
    if (page) {
      return <link rel="prev" href={buildPageUrl(page - 1)} />;
    }
    return null;
  })();

  const next = (() => {
    if (!hasNextPage(search.results, page, SEARCH_RESULT_PER_PAGE_LIMIT)) {
      return null;
    }
    return <link rel="next" href={buildPageUrl(page + 1)} />;
  })();

  const description = getPaginationDescription(search) + getSerpSeoDescription(search);

  return {
    title: getSerpSeoTitle(search),
    description,
    keywords: '',
    url: fullURL,
    canonical,
    image: url.getBaseImageUrl(),
    prev,
    next,
  };
}

export function getSearchParamsForMatchedServices(search: BaseSearchManagerOutput) {
  const fieldsRequired = ['q', 'startDate', 'endDate', 'timeOfDay', 'sort', 'prefs'];
  // delete extra keys
  Object.keys(search)
    .filter((key) => fieldsRequired.indexOf(key) === -1)
    .forEach((key) => {
      delete search[key];
    });
  return filterEmptyValues(search);
}

export function getTimeOfDateFilterLabel(timeOfDay: TimeOfDayOption): string {
  let timeOfDayLabel = '';
  const capitalizeProcessor = getLang() === 'en' ? { postProcess: 'capitalize' } : {};
  switch (timeOfDay) {
    case 'morning':
      timeOfDayLabel = _s('timeOfDay_morning', capitalizeProcessor);
      break;
    case 'afternoon':
      timeOfDayLabel = _s('timeOfDay_afternoon', capitalizeProcessor);
      break;
    case 'evening':
      timeOfDayLabel = _s('timeOfDay_evening', capitalizeProcessor);
      break;
    default:
      timeOfDayLabel = '';
      break;
  }

  return timeOfDayLabel;
}
